import { capitalize } from './string';

export const SUPPORTED_LOCALES = [
  'ar',
  'ca',
  'da',
  'de',
  'en_GB',
  'en',
  'es_419',
  'es_ES',
  'fr',
  'it',
  'nl',
  'pt_BR',
  'sv',
  'tr',
  'zh_CN',
  'zh_TW',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const fallbackLocales: Record<string, SupportedLocale> = {
  es: 'es_419',
  pt: 'pt_BR',
  zh: 'zh_CN',
};

export function convertBrowserLocale(locale: string): SupportedLocale {
  const language = locale.replace('-', '_');

  if (SUPPORTED_LOCALES.includes(language as SupportedLocale)) {
    return language as SupportedLocale;
  }

  const commonLanguage = language.substring(0, 2);

  if (SUPPORTED_LOCALES.includes(commonLanguage as SupportedLocale)) {
    return commonLanguage as SupportedLocale;
  }

  const fallbackLanguage = fallbackLocales[commonLanguage];

  if (fallbackLanguage) {
    return fallbackLanguage;
  }

  throw new Error(`Unsupported locale: ${locale}`);
}

// A locale reaching us from a URL path segment or `navigator.language` is
// untrusted input. Crawlers request paths like `/_next/...`, whose first segment
// is not a locale at all, and `toISOLocale` throws whenever the part before the
// first separator is empty. Callers handling untrusted input should test this
// first rather than catching, so the throw stays reserved for a real programming
// error. Also rejects extensions such as `en-US@posix`, which glibc reports and
// `Intl` refuses.
//
// Deliberately narrower than BCP-47: it admits only the three shapes
// `toISOLocale` normalises correctly -- language, language+region, and
// language+script+region. `en-US-u-ca-gregory` is well-formed BCP-47, but
// `toISOLocale` reads its third segment as the region and returns `en-Us-U`, so
// admitting it would put a junk tag through a redirect and a CMS fetch. A script
// needs a region beside it for the same reason: `zh-Hant` alone comes back as
// `zh-HANT`, the lone second segment being read as the region.
const REGION = '(?:[A-Za-z]{2}|[0-9]{3})'; // `GB`, plus UN M.49 codes such as `419`
const LOCALE_LIKE = new RegExp(`^[A-Za-z]{2,3}(?:[-_][A-Za-z]{4}[-_]${REGION}|[-_]${REGION})?$`);

export function isLocaleLike(value: string | undefined | null): value is string {
  return typeof value === 'string' && LOCALE_LIKE.test(value);
}

/**
 * `convertBrowserLocale`, but resolving to `fallback` rather than throwing when
 * the browser reports a language we ship no translations for. `isSupported` lets
 * the caller record the gap without treating a visitor's language as a failure.
 */
export function resolveBrowserLocale(
  locale: string,
  fallback: SupportedLocale = 'en'
): { locale: SupportedLocale; isSupported: boolean } {
  if (!isLocaleLike(locale)) {
    return { locale: fallback, isSupported: false };
  }

  try {
    return { locale: convertBrowserLocale(locale), isSupported: true };
  } catch {
    return { locale: fallback, isSupported: false };
  }
}

export function toISOLocale(locale: string) {
  const parts = locale.split(/[-_]/);

  const lang = (parts[0] ?? '').toLocaleLowerCase();

  if (!lang) {
    throw new Error('Invalid locale');
  }

  const country =
    parts.length > 1 ? ((parts.length > 2 ? parts[2] : parts[1]) ?? '').toLocaleUpperCase() : '';

  if (!country) {
    return lang;
  }

  const middle = parts.length > 2 ? capitalize(parts[1] ?? '') : '';

  return [lang, middle, country].filter(Boolean).join('-');
}

const RTL_LOCALES: Record<string, boolean> = {
  ar: true,
  // Add any other Right to left languge here
};

export function isRtlLocale(locale: string) {
  return RTL_LOCALES[locale] ?? false;
}
